import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Server, Lock, User, Check, AlertCircle, Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export const EmailSettings = () => {
  const { get, post, del } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_name: ''
  });
  const [isConfigured, setIsConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const data = await get('/email/settings');
      setSettings({
        smtp_host: data.smtp_host || '',
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || '',
        smtp_password: '', // Never return password from server
        from_name: data.from_name || ''
      });
      setIsConfigured(data.is_configured);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!settings.smtp_host || !settings.smtp_user || !settings.smtp_password) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    setSaving(true);
    try {
      await post('/email/settings', settings);
      toast.success('E-Mail-Einstellungen gespeichert');
      setIsConfigured(true);
      // Clear password field after save
      setSettings(prev => ({ ...prev, smtp_password: '' }));
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await post('/email/test', {});
      toast.success(result.message || 'Verbindung erfolgreich!');
    } catch (error) {
      toast.error(error.message || 'Verbindungstest fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await del('/email/settings');
      toast.success('E-Mail-Einstellungen gelöscht');
      setSettings({
        smtp_host: '',
        smtp_port: 587,
        smtp_user: '',
        smtp_password: '',
        from_name: ''
      });
      setIsConfigured(false);
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="email-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">E-Mail-Einstellungen</h1>
        <p className="text-gray-500 mt-1">Konfigurieren Sie Ihren persönlichen E-Mail-Server für den Versand</p>
      </div>

      {/* Status Card */}
      <Card className={isConfigured ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}>
        <CardContent className="flex items-center gap-3 py-4">
          {isConfigured ? (
            <>
              <Check className="h-5 w-5 text-green-600" />
              <span className="text-green-800 font-medium">E-Mail ist konfiguriert und einsatzbereit</span>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <span className="text-yellow-800 font-medium">E-Mail ist noch nicht konfiguriert</span>
            </>
          )}
        </CardContent>
      </Card>

      {/* Settings Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            SMTP-Server Einstellungen
          </CardTitle>
          <CardDescription>
            Geben Sie die Zugangsdaten Ihres E-Mail-Servers ein
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="email-settings-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* SMTP Host */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Server className="h-4 w-4 text-gray-400" />
                  SMTP Host *
                </Label>
                <Input
                  value={settings.smtp_host}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="z.B. smtp.office365.com, mail.ihrefirma.de"
                  data-testid="smtp-host-input"
                />
                <p className="text-xs text-gray-500">Der Hostname Ihres Mailservers</p>
              </div>

              {/* SMTP Port */}
              <div className="space-y-2">
                <Label>SMTP Port *</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
                <p className="text-xs text-gray-500">Meist 587 (TLS) oder 465 (SSL)</p>
              </div>

              {/* Username */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  Benutzername / E-Mail *
                </Label>
                <Input
                  type="email"
                  value={settings.smtp_user}
                  onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                  placeholder="ihre.email@firma.de"
                  data-testid="smtp-user-input"
                />
                <p className="text-xs text-gray-500">Ihre E-Mail-Adresse für die Anmeldung</p>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Lock className="h-4 w-4 text-gray-400" />
                  Passwort *
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={settings.smtp_password}
                    onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                    placeholder={isConfigured ? '••••••••' : 'Ihr E-Mail-Passwort'}
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? 'Verbergen' : 'Zeigen'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  {isConfigured ? 'Leer lassen, um das bestehende Passwort zu behalten' : 'Ihr E-Mail-Passwort oder App-Passwort'}
                </p>
              </div>

              {/* From Name */}
              <div className="space-y-2 md:col-span-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  Absendername (optional)
                </Label>
                <Input
                  value={settings.from_name}
                  onChange={(e) => setSettings({ ...settings, from_name: e.target.value })}
                  placeholder="z.B. Max Mustermann, Kaufmanns Consulting"
                  data-testid="from-name-input"
                />
                <p className="text-xs text-gray-500">Wird als Absendername in E-Mails angezeigt</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button 
                type="submit" 
                className="bg-[#E63946] hover:bg-[#D62839]"
                disabled={saving}
                data-testid="save-settings-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Speichern...
                  </>
                ) : (
                  'Einstellungen speichern'
                )}
              </Button>

              {isConfigured && (
                <>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleTest}
                    disabled={testing}
                    data-testid="test-connection-button"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Teste...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Verbindung testen
                      </>
                    )}
                  </Button>

                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid="delete-settings-button"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Einstellungen löschen
                  </Button>
                </>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Help Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Häufige SMTP-Einstellungen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">Microsoft 365 / Outlook</p>
              <p className="text-gray-600">Host: smtp.office365.com</p>
              <p className="text-gray-600">Port: 587</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">Gmail</p>
              <p className="text-gray-600">Host: smtp.gmail.com</p>
              <p className="text-gray-600">Port: 587</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">Eigener Server</p>
              <p className="text-gray-600">Host: mail.ihredomain.de</p>
              <p className="text-gray-600">Port: 587 oder 465</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            <strong>Hinweis:</strong> Bei Microsoft 365 und Gmail müssen Sie möglicherweise ein App-Passwort verwenden, 
            wenn die Zwei-Faktor-Authentifizierung aktiviert ist.
          </p>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>E-Mail-Einstellungen löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie Ihre E-Mail-Einstellungen wirklich löschen? 
              Sie können dann keine E-Mails mehr aus dem CRM versenden, bis Sie neue Einstellungen konfigurieren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-settings"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
